import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import { PaginationPage } from "../../components/PaginationPage";

const OverduePaymentsReport = () => {
  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  // Starts true: the report loads on open, so "No data" never shows before the first answer.
  const [loading, setLoading] = useState(true);

  const fetchReport = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/reports/overdue-payments", {
        params: { ...filters, page },
      });
      const payload = res.data;
      setRows(payload.data || payload);
      setPagination({
        current_page: payload.current_page,
        last_page: payload.last_page,
      });
    } catch (err) {
      console.error("Failed to load overdue payments report", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport(1);
    // Reason: load once when the page opens; later loads come from the Search button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPDF = async () => {
    try {
      const res = await axios.get("/api/reports/overdue-payments/pdf", {
        params: filters,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "overdue-payments-report.pdf");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Failed to download PDF", err);
    }
  };

  const downloadCSV = async () => {
    try {
      const res = await axios.get("/api/reports/overdue-payments/csv", {
        params: filters,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "overdue-payments-report.csv");
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error("Failed to download CSV", err);
    }
  };

  return (
    <div className="container py-4">
      <h4 className="fw-bold mb-3">Overdue Payments Report</h4>

      <div className="card mb-3 shadow-sm">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Area</label>
              <input
                type="text"
                className="form-control"
                placeholder="Mirpur / Uttara"
                value={filters.area || ""}
                onChange={(e) =>
                  setFilters({ ...filters, area: e.target.value || undefined })
                }
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Unit Type</label>
              <select
                className="form-select"
                value={filters.type_id || ""}
                onChange={(e) =>
                  setFilters({ ...filters, type_id: e.target.value || undefined })
                }
              >
                <option value="">All</option>
                <option value="1">Flat</option>
                <option value="2">Apartment</option>
                <option value="3">Plot</option>
                <option value="4">Parking</option>
                <option value="5">Shop</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.from_date || ""}
                onChange={(e) =>
                  setFilters({ ...filters, from_date: e.target.value || undefined })
                }
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={filters.to_date || ""}
                onChange={(e) =>
                  setFilters({ ...filters, to_date: e.target.value || undefined })
                }
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Schedule</label>
              <select
                className="form-select"
                value={filters.schedule || ""}
                onChange={(e) =>
                  setFilters({ ...filters, schedule: e.target.value || undefined })
                }
              >
                <option value="">All</option>
                <option value="Monthly">Monthly</option>
                <option value="Weekly">Weekly</option>
                <option value="15days">15days</option>
              </select>
            </div>

            <div className="col-md-3">
              <label className="form-label">Min Days Overdue</label>
              <input
                type="number"
                className="form-control"
                min="0"
                value={filters.min_days_overdue || ""}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    min_days_overdue: e.target.value || undefined,
                  })
                }
              />
            </div>
          </div>

          <div className="mt-3 d-flex justify-content-end gap-2">
            <button className="btn btn-primary" onClick={() => fetchReport(1)}>
              {loading ? "Loading..." : "Search"}
            </button>
            <button className="btn btn-outline-danger" onClick={downloadPDF}>
              PDF Download
            </button>
            <button className="btn btn-outline-secondary" onClick={downloadCSV}>
              CSV Download
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body table-responsive">
          <table className="table table-bordered table-striped align-middle">
            <thead>
              <tr>
                <th>Property</th>
                <th>Unit</th>
                <th>Customer</th>
                <th>Installment No</th>
                <th>Schedule</th>
                <th>Due Date</th>
                <th>Grace Days</th>
                <th>Overdue Since</th>
                <th>Days Overdue</th>
                <th>Installment Due</th>
                <th>Booking Due</th>
                <th>Late Fee Total</th>
                <th>Late Fee Status</th>
                <th>Past Overdue Count</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="14" className="text-center">
                    {loading ? "Loading..." : "No data"}
                  </td>
                </tr>
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id || idx}>
                    <td>{row.booking?.property?.name || "—"}</td>
                    <td>{row.booking?.unit?.unit_number || "—"}</td>
                    <td>{row.customer?.name || "—"}</td>
                    <td>{row.installment_number}</td>
                    <td>{row.plan_name || "—"}</td>
                    <td>{row.due_date || "—"}</td>
                    <td>{row.grace_period_days ?? "—"}</td>
                    <td>{row.overdue_since || "—"}</td>
                    <td className="text-danger fw-bold">{row.days_overdue ?? "—"}</td>
                    <td>{Number(row.due_amount || 0).toFixed(2)}</td>
                    <td>{Number(row.booking?.due_amount || 0).toFixed(2)}</td>
                    <td>{Number(row.late_fee_total || 0).toFixed(2)}</td>
                    <td>{row.late_fee_status || "—"}</td>
                    <td>{row.customer_past_overdue_count ?? 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <PaginationPage
            pagination={pagination}
            onPageChange={(page) => fetchReport(page)}
          />
        </div>
      </div>
    </div>
  );
};

export default OverduePaymentsReport;

